import React, { useState, useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import tw from "twin.macro";
import Sidebar from "../ScaldComponets/Sidebar";
import HeaderContainer from "../ScaldComponets/WarehouseAccountingHeader";
import { useLocation, useNavigate } from "react-router-dom";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "../../../FireBaseConfig";
import { Toaster, toast } from "react-hot-toast";

const GlobalStyle = createGlobalStyle`
    @font-face {
        font-family: 'SFUIText';
        font-weight: 300;
        font-style: normal;
    }

    body, html {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
        font-family: 'SFUIText', sans-serif;
    }
`;

const Container = styled.div`
    ${tw`min-h-screen flex flex-col`}
`;

const MainContent = styled.div`
    ${tw`flex flex-grow`}
`;

const Content = styled.div`
    ${tw`flex-grow p-8`}
    display: flex;
    flex-direction: column;
    background-color: #fff;
    border-radius: 10px;
`;

const SectionTitle = styled.h2`
    ${tw`text-lg font-bold mb-6`}
    color: #0ABD19;
    border-bottom: 2px solid #0ABD19;
    padding-bottom: 10px;
`;

const InfoBox = styled.div`
    ${tw`mb-8 p-6 rounded-lg`}
    background-color: #FAFAFA;
    border: 1px solid #E0E0E0;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
`;

const InfoItem = styled.div`
    ${tw`mb-4`}
`;

const InfoLabel = styled.div`
    ${tw`text-sm font-semibold text-gray-600 mb-1`}
`;

const InfoValue = styled.div`
    ${tw`text-base text-gray-800`}
`;

const Row = styled.div`
    ${tw`grid grid-cols-2 gap-8 mb-6`}
`;

const Label = styled.label`
    ${tw`block text-sm font-semibold mb-2 text-gray-600`}
`;

const Input = styled.input`
    ${tw`w-full p-3 rounded-lg border`}
    border: 1px solid #E0E0E0;

    &:focus {
        border-color: #4CAF50;
        box-shadow: none;
        outline: none;
    }
`;

const Select = styled.select`
    ${tw`w-full p-3 rounded-lg border`}
    border: 1px solid #E0E0E0;

    &:focus {
        border-color: #4CAF50;
        outline: none;
    }
`;

const TextArea = styled.textarea`
    ${tw`w-full p-3 rounded-lg border`}
    border: 1px solid #E0E0E0;
    min-height: 100px;
    resize: vertical;

    &:focus {
        border-color: #4CAF50;
        outline: none;
    }
`;

const ButtonContainer = styled.div`
    ${tw`flex justify-start gap-4 mt-8`}
`;

const GreenButton = styled.button`
    ${tw`bg-green-1002 text-white py-2 px-20 rounded-lg`}
    border: none;
    &:hover {
        background-color: #0ABD19;
    }
`;

const GrayButton = styled.button`
    ${tw`bg-gray-200 text-gray-900 py-2 px-20 rounded-lg`}
    border: none;
    &:hover {
        background-color: #ddd;
    }
`;

const FormEditingService = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const item = location.state?.item;

    const [formData, setFormData] = useState({
        userId: "",
        packageId: "",
        trackingNumber: "",
        service: "",
        country: "",
        comments: "",
        cost: "",
        status: "",
        adminNotes: ""
    });

    useEffect(() => {
        if (!item) {
            toast.error("Данные заявки не найдены");
            navigate("/servicesclad");
            return;
        }

        const docRef = doc(db, "applications", item.id);
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setFormData({
                    userId: data.userId || "",
                    packageId: data.packageId || "",
                    trackingNumber: data.trackingNumber || "",
                    service: data.service || "",
                    country: data.country || "",
                    comments: data.comments || "",
                    cost: data.cost || "",
                    status: data.status || "",
                    adminNotes: data.adminNotes || ""
                });
            }
        });

        return () => unsubscribe();
    }, [item, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = async () => {
        try {
            const docRef = doc(db, "applications", item.id);
            await updateDoc(docRef, {
                status: formData.status,
                cost: parseFloat(formData.cost) || 0,
                adminNotes: formData.adminNotes
            });

            toast.success("Заявка успешно обновлена!");
            setTimeout(() => {
                navigate("/servicesclad");
            }, 1500);
        } catch (error) {
            console.error("Ошибка при сохранении:", error);
            toast.error("Ошибка при сохранении данных");
        }
    };

    const handleCancel = () => {
        navigate("/servicesclad");
    };

    return (
        <Container>
            <GlobalStyle />
            <Toaster position="top-right" />
            <HeaderContainer />
            <MainContent>
                <Sidebar />
                <Content>
                    <SectionTitle>Редактирование заявки на услугу</SectionTitle>

                    <InfoBox>
                        <InfoItem>
                            <InfoLabel>ID Клиента:</InfoLabel>
                            <InfoValue>{formData.userId}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>ID Посылки:</InfoLabel>
                            <InfoValue>{formData.packageId}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Трек-номер:</InfoLabel>
                            <InfoValue>{formData.trackingNumber}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Услуга:</InfoLabel>
                            <InfoValue>{formData.service}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Страна:</InfoLabel>
                            <InfoValue>{formData.country}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Комментарий клиента:</InfoLabel>
                            <InfoValue>{formData.comments || "—"}</InfoValue>
                        </InfoItem>
                    </InfoBox>

                    <SectionTitle>Управление заявкой</SectionTitle>

                    <Row>
                        <div>
                            <Label>Статус</Label>
                            <Select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                            >
                                <option value="Создана">Создана</option>
                                <option value="В работе">В работе</option>
                                <option value="Завершено">Завершено</option>
                                <option value="Отменена">Отменена</option>
                            </Select>
                        </div>
                        <div>
                            <Label>Стоимость ($)</Label>
                            <Input
                                type="number"
                                name="cost"
                                value={formData.cost}
                                onChange={handleInputChange}
                                step="0.01"
                                min="0"
                            />
                        </div>
                    </Row>

                    <div>
                        <Label>Заметки администратора</Label>
                        <TextArea
                            name="adminNotes"
                            value={formData.adminNotes}
                            onChange={handleInputChange}
                            placeholder="Введите внутренние заметки о заявке..."
                        />
                    </div>

                    <ButtonContainer>
                        <GreenButton onClick={handleSave}>Сохранить</GreenButton>
                        <GrayButton onClick={handleCancel}>Отмена</GrayButton>
                    </ButtonContainer>
                </Content>
            </MainContent>
        </Container>
    );
};

export default FormEditingService;
